/**
 * MeasurementEquipment Edit Page
 * Page for editing existing measurement equipment
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { measurementEquipmentService } from '../../services/measurementEquipmentService';
import { MeasurementEquipment, MeasurementEquipmentFormData } from '../../types/measurementEquipment';
import { MeasurementEquipmentForm } from './components/MeasurementEquipmentForm';

export const MeasurementEquipmentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [equipment, setEquipment] = useState<MeasurementEquipment | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    const loadEquipment = async () => {
      if (!id) return;

      try {
        setLoadingData(true);
        const data = await measurementEquipmentService.getMeasurementEquipmentById(Number(id));
        setEquipment(data);
      } catch (err: unknown) {
        setFalha(
          falhaDeCarga(err, 'Não foi possível carregar o equipamento de medição. Tente de novo.'),
        );
      } finally {
        setLoadingData(false);
      }
    };

    loadEquipment();
  }, [id]);

  const handleSubmit = async (data: MeasurementEquipmentFormData) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      await measurementEquipmentService.updateMeasurementEquipment(Number(id), data);
      navigate('/measurement-equipments');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar equipamento de medição';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/measurement-equipments');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Equipamentos de Medição', path: '/measurement-equipments' },
    { label: 'Editar Equipamento' },
  ];

  if (loadingData) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Equipamento de Medição">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!equipment) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Equipamento de Medição">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Equipamento de medição não encontrado.')}
          rotuloDoBotao="Voltar para equipamentos de medição"
          aoVoltar={() => navigate('/measurement-equipments')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Equipamento de Medição"
      subtitle={equipment.name}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <MeasurementEquipmentForm
        initialData={equipment}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />
    </PageShell>
  );
};

export default MeasurementEquipmentEditPage;
